// src/pages/MyDay.js — the MIDTC Assistant in the browser.
// Personal task list built from your assigned tickets/projects and your own
// inbox, an AI-built day plan, calendar delivery (invite emails + subscribable
// feed), and the enrolment code that binds the desktop assistant to you alone.
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, Sparkles, CalendarPlus, Link2, Play, Square, Check, Trash2, Plus, Monitor, Copy, CalendarClock, Download } from "lucide-react";
import { fmtDur } from "../components/TimeTracker";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (p, o = {}) =>
  fetch(API + p, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...o });

const PRIO = { Critical: "#ef4444", High: "#f97316", Medium: "#fbbf24", Low: "#34d399" };
const SRC = { ticket: "🎫", project: "🏗️", email: "✉️", manual: "📝", ai: "🤖", lead: "🎯" };
const hhmm = (d) => d ? new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
// A block on another day says which day — a bare "9:00 AM" read as today.
const when = (d) => {
  if (!d) return null;
  const x = new Date(d);
  return x.toDateString() === new Date().toDateString() ? hhmm(d) : `${x.toLocaleDateString("en-US", { weekday: "short" })} ${hhmm(d)}`;
};
// Daily plan (dailyRecap.js): the same plan the 5:30 PM / midnight recap email carries.
const ACT = { reply: ["Reply", "#3b82f6"], follow_up: ["Follow up", "#06b6d4"], escalate: ["Escalate", "#ef4444"], work: ["Work on", "#8b5cf6"], close: ["Close", "#22c55e"] };
const dayName = (ymd) => ymd ? new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" }) : "";
const todayEt = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export default function MyDay() {
  const navigate = useNavigate();
  const location = useLocation();
  const [daily, setDaily]   = useState(null);   // { plan, created_at }
  const [tasks, setTasks]   = useState([]);
  const [active, setActive] = useState(null);
  const [today, setToday]   = useState(0);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState("");
  const [note, setNote]     = useState(null);
  const [plan, setPlan]     = useState(null);
  const [newTask, setNewTask] = useState("");
  const [setup, setSetup]   = useState(null);   // {code} enrolment
  const [devices, setDevices] = useState([]);
  const [calUrl, setCalUrl] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [asstVersion, setAsstVersion] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/assistant/me").then(r => r.json());
      setTasks(d.tasks || []); setActive(d.active_session || null); setToday(d.today_seconds || 0);
    } finally { setLoad(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiFetch("/assistant/devices").then(r => r.json()).then(d => setDevices(Array.isArray(d) ? d : [])).catch(() => {}); }, [showSetup]);
  // Label the download button with the version the server is actually serving,
  // so the page can never advertise a build that is no longer current.
  useEffect(() => { apiFetch("/assistant/version").then(r => r.json()).then(d => setAsstVersion(d?.version || "")).catch(() => {}); }, []);

  const run = async (key, fn) => { setBusy(key); setNote(null); try { await fn(); } finally { setBusy(""); } };

  const loadPlan = useCallback(async () => {
    try { const d = await apiFetch("/assistant/daily-plan").then(r => r.json()); setDaily(d?.plan ? d : null); } catch { /* the page works without a plan */ }
  }, []);
  useEffect(() => { loadPlan(); }, [loadPlan]);

  const refreshPlan = () => run("dplan", async () => {
    const r = await apiFetch("/assistant/daily-plan/refresh", { method: "POST", body: JSON.stringify({}) });
    const d = await r.json();
    if (!r.ok) { setNote({ ok: false, text: d.error || "Couldn't make a plan — try again." }); return; }
    setDaily(d); setNote({ ok: true, text: "Your plan is up to date." }); load();
  });

  // Book a plan ticket on the calendar: the plan's time if it is still ahead, else the next free slot.
  const planToCalendar = async (ticketId) => {
    setBusy("cal-" + ticketId); setNote(null);
    try {
      const r = await apiFetch("/assistant/daily-plan/schedule", { method: "POST", body: JSON.stringify({ ticket_id: ticketId }) });
      const d = await r.json();
      if (!r.ok) { setNote({ ok: false, text: d.error || "Couldn't add it to your calendar." }); return; }
      setNote({ ok: true, text: `Booked for ${new Date(d.task.scheduled_start).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })} — the calendar invitation is on its way to your inbox.` });
      load();
    } finally { setBusy(""); }
  };

  // "Add to my calendar" links in the recap email land here as /my-day?schedule=<ticket id>.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get("schedule");
    if (!id) return;
    navigate("/my-day", { replace: true });
    planToCalendar(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = () => run("sync", async () => {
    const d = await apiFetch("/assistant/sync", { method: "POST", body: JSON.stringify({}) }).then(r => r.json());
    const c = d.created || {};
    setNote({ ok: true, text: `Synced — ${c.tickets || 0} ticket(s), ${c.projects || 0} project(s), ${c.emails || 0} email(s) added.` });
    load();
  });

  const buildPlan = () => run("plan", async () => {
    const r = await apiFetch("/assistant/plan", { method: "POST", body: JSON.stringify({ start_hour: 9, end_hour: 17 }) });
    const d = await r.json();
    if (!r.ok) { setNote({ ok: false, text: d.error || "Planning failed" }); return; }
    setPlan(d);
    setNote({ ok: true, text: `${d.scheduled} block(s) scheduled.` });
    load();
  });

  const sendInvites = () => run("invite", async () => {
    const r = await apiFetch("/assistant/calendar/invite", { method: "POST", body: JSON.stringify({}) });
    const d = await r.json();
    setNote({ ok: r.ok, text: d.message || d.error });
  });

  const getCalUrl = () => run("cal", async () => {
    const d = await apiFetch("/assistant/calendar-url").then(r => r.json());
    setCalUrl(d.url);
  });

  const enroll = () => run("enroll", async () => {
    const d = await apiFetch("/assistant/enroll/code", { method: "POST" }).then(r => r.json());
    setSetup(d); setShowSetup(true);
  });

  const setStatus = async (t, status) => {
    await apiFetch(`/assistant/tasks/${t.id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    load();
  };
  const del = async (t) => { await apiFetch(`/assistant/tasks/${t.id}`, { method: "DELETE" }); load(); };
  const add = async () => {
    if (!newTask.trim()) return;
    await apiFetch("/assistant/tasks", { method: "POST", body: JSON.stringify({ title: newTask.trim() }) });
    setNewTask(""); load();
  };

  const timer = async (t, start) => {
    if (start) {
      if (!t.entity_type || !t.entity_id) { setNote({ ok: false, text: "This task isn't linked to a ticket or project, so there's nothing to time against." }); return; }
      await apiFetch("/time/start", { method: "POST", body: JSON.stringify({ entity_type: t.entity_type, entity_id: t.entity_id }) });
    } else await apiFetch("/time/stop", { method: "POST", body: JSON.stringify({}) });
    load();
  };

  const scheduleOne = async (t) => {
    const r = await apiFetch(`/assistant/tasks/${t.id}/schedule`, { method: "POST", body: JSON.stringify({}) });
    const d = await r.json();
    if (!r.ok) { setNote({ ok: false, text: d.error || "Could not schedule" }); return; }
    setNote({ ok: true, text: `“${d.title}” scheduled for ${new Date(d.scheduled_start).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}.` });
    load();
  };

  const inviteOne = async (t) => {
    const r = await apiFetch("/assistant/calendar/invite", { method: "POST", body: JSON.stringify({ task_id: t.id, resend: true }) });
    const d = await r.json();
    setNote({ ok: r.ok, text: d.message || d.error });
    load();
  };

  const openTask = (t) => {
    if (t.entity_type === "ticket") navigate(`/tickets/${t.entity_id}`);
    else if (t.entity_type === "project") navigate(`/projects?project=${t.entity_id}`);
    // Lead follow-up reminders (leadGen.js): the lead id rides in external_ref "leadgen:<id>".
    else if (t.entity_type === "lead") navigate(`/lead-generation?lead=${String(t.external_ref || "").split(":")[1]}`);
  };

  const scheduled = tasks.filter(t => t.scheduled_start).sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  const unscheduled = tasks.filter(t => !t.scheduled_start);
  const tool = { display: "inline-flex", alignItems: "center", gap: 7, background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 12, padding: 16 };

  const TaskRow = ({ t }) => {
    const running = active && active.entity_type === t.entity_type && String(active.entity_id) === String(t.entity_id);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: "1px solid var(--t-border-soft)", background: running ? "rgba(52,211,153,0.06)" : "transparent" }}>
        <button onClick={() => setStatus(t, t.status === "done" ? "todo" : "done")} title="Mark done"
          style={{ width: 17, height: 17, borderRadius: 4, flexShrink: 0, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center",
            border: `2px solid ${t.status === "done" ? "#34d399" : "var(--t-border-strong, #475569)"}`, background: t.status === "done" ? "#34d399" : "transparent" }}>
          {t.status === "done" && <Check size={11} color="#fff" />}
        </button>
        <span style={{ fontSize: 13, flexShrink: 0 }}>{SRC[t.source] || "•"}</span>
        {t.scheduled_start && (
          <span style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", color: "#60a5fa", minWidth: 96, flexShrink: 0 }}>
            {when(t.scheduled_start)}–{hhmm(t.scheduled_end)}
          </span>
        )}
        <span onClick={() => openTask(t)}
          style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--t-text-body)", cursor: t.entity_id || t.entity_type === "lead" ? "pointer" : "default", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: t.status === "done" ? "line-through" : "none" }}
          title={t.detail || t.title}>
          {t.title}
        </span>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: PRIO[t.priority] || "#64748b", flexShrink: 0 }} title={t.priority} />
        {!t.scheduled_start && (
          <button onClick={() => scheduleOne(t)} title="Not on your schedule — find a slot"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(79,111,255,0.12)", border: "1px solid rgba(79,111,255,0.35)", color: "#93c5fd", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
            <CalendarClock size={11} /> Schedule
          </button>
        )}
        {t.scheduled_start && !t.invite_sent_at && (
          <button onClick={() => inviteOne(t)} title="Scheduled, but not on your calendar yet — send the invitation"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.35)", color: "#fbbf24", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
            <CalendarPlus size={11} /> Add to calendar
          </button>
        )}
        {t.scheduled_start && t.invite_sent_at && (
          <span title="Invitation sent — accept it in your mail to put it on your calendar" style={{ fontSize: 10.5, color: "#34d399", fontWeight: 700, flexShrink: 0 }}>on calendar</span>
        )}
        {t.entity_id && (
          running
            ? <button onClick={() => timer(t, false)} title="Stop timer" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", borderRadius: 6, padding: "3px 8px", cursor: "pointer", display: "flex" }}><Square size={12} /></button>
            : <button onClick={() => timer(t, true)} title="Start timer" style={{ background: "none", border: "1px solid var(--t-border-soft)", color: "#34d399", borderRadius: 6, padding: "3px 8px", cursor: "pointer", display: "flex" }}><Play size={12} /></button>
        )}
        <button onClick={() => del(t)} style={{ background: "none", border: "none", color: "var(--t-border-strong, #475569)", cursor: "pointer", padding: 0, display: "flex" }}><Trash2 size={13} /></button>
      </div>
    );
  };

  // ── Daily plan panel (v2: only the key items the AI picked — top 3–5, the day in blocks) ──
  const P = daily?.plan;
  const oldPlan = P && !P.priorities;   // saved before v2 — ask for a refresh rather than show the long list
  const stalePlan = oldPlan || (P?.plan_date && P.plan_date < todayEt());
  const label = { fontSize: 11.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" };
  const headRow = { ...label, padding: "11px 22px", borderTop: "1px solid var(--t-border-soft)", borderBottom: "1px solid var(--t-border-soft)", background: "var(--t-input)" };
  const taskFor = (tid) => tasks.find(t => t.entity_type === "ticket" && String(t.entity_id) === String(tid));
  const CalBtn = ({ tid }) => {
    const t = taskFor(tid);
    if (t?.scheduled_start) return (
      <span title={t.invite_sent_at ? "Invitation sent — accept it to put it on your calendar" : "Booked on My Day"}
        style={{ fontSize: 12, fontWeight: 700, color: t.invite_sent_at ? "#34d399" : "#fbbf24", flexShrink: 0, whiteSpace: "nowrap" }}>
        {t.invite_sent_at ? "On calendar" : "Booked"} · {when(t.scheduled_start)}
      </span>
    );
    return (
      <button onClick={() => planToCalendar(tid)} disabled={busy === "cal-" + tid} title="Book a block and send the calendar invitation"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(79,111,255,0.12)", border: "1px solid rgba(79,111,255,0.35)", color: "#93c5fd", borderRadius: 7, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, whiteSpace: "nowrap" }}>
        <CalendarPlus size={13} /> {busy === "cal-" + tid ? "Adding…" : "Add to calendar"}
      </button>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>My Day</h1>
          <p style={{ margin: "4px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>
            {tasks.length} open task{tasks.length !== 1 ? "s" : ""} · {fmtDur(today)} tracked today
            {active && <span style={{ color: "#34d399", fontWeight: 600 }}> · timer running</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={tool} onClick={sync}><RefreshCw size={13} /> {busy === "sync" ? "Syncing…" : "Sync my work"}</button>
          <button style={{ ...tool, background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none" }} onClick={buildPlan}>
            <Sparkles size={13} /> {busy === "plan" ? "Planning…" : "Plan my day"}
          </button>
        </div>
      </div>

      {note && (
        <div style={{ marginBottom: 14, background: note.ok ? "rgba(52,211,153,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${note.ok ? "rgba(52,211,153,0.3)" : "rgba(239,68,68,0.3)"}`, color: note.ok ? "#34d399" : "#f87171", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
          {note.text}
        </div>
      )}
      {P ? (
        <div style={{ ...panel, marginBottom: 18, padding: 0, overflow: "hidden", borderTop: "3px solid var(--t-accent, #4f6fff)" }}>
          <div style={{ padding: "20px 22px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, flex: "1 1 460px" }}>
              <div style={label}>🎯 Key goal for {dayName(P.plan_date)}</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: "var(--t-text-primary)", lineHeight: 1.45, marginTop: 8 }}>{P.goal}</div>
              {P.targets && (
                <div style={{ fontSize: 13.5, color: "var(--t-text-faint)", marginTop: 10 }}>
                  Reply <b style={{ color: "var(--t-text-primary)" }}>{P.targets.reply ?? 0}</b> · Follow up <b style={{ color: "var(--t-text-primary)" }}>{P.targets.follow_up ?? 0}</b> · Close <b style={{ color: "var(--t-text-primary)" }}>{P.targets.close ?? 0}</b> · Touch <b style={{ color: "var(--t-text-primary)" }}>{P.targets.touch ?? 0}</b>
                </div>
              )}
              {P.motivation && (
                <div style={{ fontSize: 14.5, color: "var(--t-text-body)", lineHeight: 1.6, marginTop: 12, fontStyle: "italic" }}>{P.motivation}</div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
              <button style={stalePlan ? { ...tool, color: "#fbbf24", borderColor: "rgba(251,191,36,0.45)" } : tool} onClick={refreshPlan}>
                <RefreshCw size={13} /> {busy === "dplan" ? "Making your plan…" : "Refresh plan"}
              </button>
              <span style={{ fontSize: 11.5, color: stalePlan ? "#fbbf24" : "var(--t-text-faint)" }}>
                {oldPlan ? "Refresh for the new, shorter plan" : stalePlan ? "This plan is from an earlier day" : `Made ${new Date(daily.created_at).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}`}
                {P.ai === false ? " · basic plan" : ""}
              </span>
            </div>
          </div>
          {P.priorities?.length > 0 && (
            <>
              <div style={headRow}>Your top {P.priorities.length}</div>
              {P.priorities.map((p, i) => {
                const [name, color] = ACT[p.action] || ACT.work;
                return (
                  <div key={p.ticket_id} style={{ display: "flex", gap: 14, padding: "16px 22px", borderBottom: "1px solid var(--t-border-soft)", alignItems: "flex-start", flexWrap: "wrap" }}>
                    <span style={{ width: 28, height: 28, borderRadius: "50%", background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                    <div style={{ flex: "1 1 360px", minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color }}>{p.time} · {name}</div>
                      <div onClick={() => navigate(`/tickets/${p.ticket_id}`)} style={{ fontSize: 15.5, fontWeight: 700, color: "var(--t-text-primary)", cursor: "pointer", marginTop: 3, lineHeight: 1.4 }}>{p.ticket} — {p.subject}</div>
                      <div style={{ fontSize: 12.5, color: "var(--t-text-faint)", marginTop: 1 }}>{p.client}</div>
                      <div style={{ fontSize: 14, color: "var(--t-text-body)", lineHeight: 1.6, marginTop: 8 }}>{p.why}</div>
                      <div style={{ fontSize: 14, color: "var(--t-text-body)", lineHeight: 1.6, marginTop: 3 }}><b style={{ color: "var(--t-text-primary)" }}>Start with:</b> {p.first_step}</div>
                    </div>
                    <CalBtn tid={p.ticket_id} />
                  </div>
                );
              })}
            </>
          )}
          {P.day?.length > 0 && (
            <>
              <div style={headRow}>How to shape your day</div>
              <div style={{ padding: "10px 22px 8px" }}>
                {P.day.map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, padding: "8px 0", flexWrap: "wrap" }}>
                    <div style={{ width: 160, flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t-text-primary)" }}>{b.label}</div>
                      <div style={{ fontSize: 12, color: "var(--t-text-faint)", marginTop: 1 }}>{b.time}</div>
                    </div>
                    <div style={{ flex: "1 1 240px", minWidth: 0, fontSize: 14, color: "var(--t-text-body)", lineHeight: 1.55 }}>
                      {b.focus}
                      {b.tickets?.length > 0 && (
                        <div style={{ marginTop: 3, display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {b.tickets.map(t => (
                            <span key={t.ticket_id} onClick={() => navigate(`/tickets/${t.ticket_id}`)} style={{ fontSize: 12.5, fontWeight: 700, color: "#60a5fa", cursor: "pointer" }}>{t.ticket}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {(P.highlights?.length > 0 || P.coach) && (
            <div style={{ padding: "14px 22px 18px", borderTop: "1px solid var(--t-border-soft)" }}>
              {P.highlights?.map((h, i) => (
                <div key={i} style={{ fontSize: 14, color: "var(--t-text-body)", lineHeight: 1.6, display: "flex", gap: 9 }}>
                  <span style={{ color: "#34d399", fontWeight: 800 }}>✓</span><span>{h}</span>
                </div>
              ))}
              {P.coach && <div style={{ fontSize: 13.5, color: "var(--t-text-faint)", marginTop: P.highlights?.length ? 8 : 0, fontStyle: "italic" }}>{P.coach}</div>}
            </div>
          )}
        </div>
      ) : (
        <div style={{ ...panel, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, color: "var(--t-text-body)", lineHeight: 1.6 }}>
            <b style={{ color: "var(--t-text-primary)" }}>🎯 No plan yet.</b> Your daily recap builds one at 5:30 PM and midnight, or make one now from your queue.
          </div>
          <button style={{ ...tool, background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none" }} onClick={refreshPlan}>
            <Sparkles size={13} /> {busy === "dplan" ? "Making your plan…" : "Make my plan"}
          </button>
        </div>
      )}

      {plan?.summary && (
        <div style={{ ...panel, marginBottom: 14, borderLeft: "3px solid #7b61ff" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#a78bfa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>🤖 Today's plan</div>
          <div style={{ fontSize: 13.5, color: "var(--t-text-body)", lineHeight: 1.65 }}>{plan.summary}</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(260px,1fr)", gap: 16, alignItems: "start" }}>
        {/* Tasks */}
        <div style={{ ...panel, padding: 0, overflow: "hidden" }}>
          {loading && <div style={{ padding: 24, color: "#475569", fontSize: 13 }}>Loading…</div>}
          {!loading && !tasks.length && (
            <div style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13, lineHeight: 1.7 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>☀️</div>
              Nothing here yet — hit <b>Sync my work</b> to pull in your assigned tickets, projects and unread mail.
            </div>
          )}
          {scheduled.length > 0 && (
            <>
              <div style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--t-border-soft)" }}>Scheduled</div>
              {scheduled.map(t => <TaskRow key={t.id} t={t} />)}
            </>
          )}
          {unscheduled.length > 0 && (
            <>
              <div style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--t-border-soft)", borderTop: scheduled.length ? "1px solid var(--t-border-soft)" : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Not on your schedule ({unscheduled.length})</span>
                <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#64748b" }}>Schedule finds your next free slot</span>
              </div>
              {unscheduled.map(t => <TaskRow key={t.id} t={t} />)}
            </>
          )}
          <div style={{ display: "flex", gap: 6, padding: 10, borderTop: "1px solid var(--t-border-soft)" }}>
            <input value={newTask} onChange={e => setNewTask(e.target.value)} onKeyDown={e => e.key === "Enter" && add()}
              placeholder="Add a task…" style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 10px", color: "var(--t-text-primary)", fontSize: 12.5 }} />
            <button onClick={add} style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}><Plus size={13} /> Add</button>
          </div>
        </div>

        {/* Side rail */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={panel}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>📅 Get this on your calendar</div>
            <button style={{ ...tool, width: "100%", justifyContent: "center", marginBottom: 8 }} onClick={sendInvites}>
              <CalendarPlus size={13} /> {busy === "invite" ? "Sending…" : "Email me invitations"}
            </button>
            <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.6, marginBottom: 10 }}>
              Sends each scheduled block as a calendar invitation — accept it and it lands on your Outlook or Apple calendar.
            </div>
            <button style={{ ...tool, width: "100%", justifyContent: "center" }} onClick={getCalUrl}>
              <Link2 size={13} /> {busy === "cal" ? "…" : "Subscribe link (auto-updating)"}
            </button>
            {calUrl && (
              <div style={{ marginTop: 8 }}>
                <input readOnly value={calUrl} onFocus={e => e.target.select()}
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 8px", color: "var(--t-text-body)", fontSize: 10.5, fontFamily: "monospace" }} />
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.55 }}>
                  Add as a subscribed calendar: Outlook → Add calendar → Subscribe from web · Apple Calendar → File → New Calendar Subscription.
                </div>
              </div>
            )}
          </div>

          <div style={panel}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>💻 Desktop assistant</div>
            <button style={{ ...tool, width: "100%", justifyContent: "center" }} onClick={enroll}>
              <Monitor size={13} /> {busy === "enroll" ? "…" : "Set up on this machine"}
            </button>
            {setup?.code && (
              <div style={{ marginTop: 10, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10.5, color: "#64748b", marginBottom: 6, fontWeight: 700, textTransform: "uppercase" }}>Your one-time code · {setup.expires_in_minutes} min</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{ flex: 1, fontSize: 13, fontWeight: 800, color: "#34d399", letterSpacing: "0.5px" }}>{setup.code}</code>
                  <button onClick={() => navigator.clipboard?.writeText(setup.code)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", display: "flex" }}><Copy size={14} /></button>
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.55 }}>
                  Paste this into the assistant on first launch. It binds that install to <b>you</b> — nobody else's work will appear in it.
                </div>
              </div>
            )}
            {devices.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {devices.filter(d => !d.revoked_at).map(d => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: d.last_seen_at && Date.now() - new Date(d.last_seen_at) < 6e5 ? "#34d399" : "#64748b" }} />
                    <span style={{ flex: 1, minWidth: 0, color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.device_name}</span>
                    <button onClick={async () => { await apiFetch(`/assistant/devices/${d.id}`, { method: "DELETE" }); setShowSetup(s => !s); }}
                      style={{ background: "none", border: "none", color: "#f87171", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Revoke</button>
                  </div>
                ))}
              </div>
            )}
            {/* Direct download. /downloads/assistant/latest globs the dist folder and
                serves the newest build, so this link does not need touching on a
                release — unlike a version-pinned filename. */}
            <a href={`${API.replace(/\/api$/, "")}/downloads/assistant/latest`}
               style={{ ...tool, width: "100%", justifyContent: "center", marginTop: 8, textDecoration: "none", color: "#93c5fd", background: "rgba(79,111,255,0.12)", border: "1px solid rgba(79,111,255,0.35)" }}>
              <Download size={13} /> Download the assistant{asstVersion ? ` ${asstVersion}` : ""}
            </a>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.55 }}>
              Windows installer. It replaces any older version in place and keeps you signed in —
              you only need a code above for a machine that has never had it.
              Also on the <b onClick={() => navigate("/software")} style={{ color: "#60a5fa", cursor: "pointer" }}>Software</b> page.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
